import React, { useRef, useState } from 'react';

const INBOX_DRAG_TYPE = 'application/x-mindmappi-inbox-idea';

export { INBOX_DRAG_TYPE };

const stopCanvasEvent = (event) => event.stopPropagation();

function InboxIcon({ size = 18 }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 5h16v14H4z" />
      <path d="M4 14h4l2 2h4l2-2h4" />
    </svg>
  );
}

function InboxCard({
  node,
  branchCount,
  isMobile,
  canWrite,
  onDelete,
  onDragStart,
  onDragEnd,
}) {
  const canDragToMap = canWrite && !isMobile;
  const cardStatus = branchCount > 0
    ? `${branchCount} ${branchCount === 1 ? 'child' : 'children'}${canDragToMap ? ' · Drag onto an idea' : ''}`
    : canDragToMap ? 'Drag onto an idea' : node.creatorName || 'Captured idea';

  return (
    <article
      className="idea-inbox__card"
      draggable={canDragToMap}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData(INBOX_DRAG_TYPE, node.id);
        event.dataTransfer.setData('text/plain', `mindmappi-inbox:${node.id}`);
        onDragStart?.(node.id);
      }}
      onDragEnd={() => onDragEnd?.()}
      data-inbox-idea-id={node.id}
      title={canDragToMap ? 'Drag onto an idea in the map' : undefined}
    >
      <div className="idea-inbox__card-text">{node.text}</div>
      {!!node.tags?.length && (
        <div className="idea-inbox__tags">
          {node.tags.map((tag) => <span key={tag}>#{tag}</span>)}
        </div>
      )}
      <footer>
        <span>{cardStatus}</span>
        {canWrite && (
          <div>
            <button type="button" onClick={() => onDelete(node.id)} aria-label="Delete inbox idea">
              ×
            </button>
          </div>
        )}
      </footer>
    </article>
  );
}

export default function IdeaInbox({
  mode,
  isMobile,
  mobileView,
  isDarkMode,
  inbox,
  captureTarget,
  canWrite,
  isDropActive,
  onModeChange,
  onMobileViewChange,
  onAddIdea,
  onDeleteIdea,
  onInboxDragStart,
  onInboxDragEnd,
}) {
  const [draft, setDraft] = useState('');
  const [draftTarget, setDraftTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const composerRef = useRef(null);
  const effectiveTarget = draft.trim() ? draftTarget : captureTarget;

  const updateDraft = (value) => {
    if (!draft && value) setDraftTarget(captureTarget || null);
    if (!value) setDraftTarget(null);
    setDraft(value);
  };

  const submit = async () => {
    if (!draft.trim() || submitting || !canWrite) return;
    setSubmitting(true);
    try {
      await onAddIdea(draft, effectiveTarget?.id || null);
      setDraft('');
      setDraftTarget(null);
      window.requestAnimationFrame(() => composerRef.current?.focus());
    } finally {
      setSubmitting(false);
    }
  };

  const mobileTabs = isMobile && (
    <nav className="idea-inbox__mobile-tabs" data-export-ignore="true" data-viewport-occlusion="true">
      <button
        type="button"
        className={mobileView === 'ideas' ? 'is-active' : ''}
        onClick={() => onMobileViewChange('ideas')}
      >
        Ideas {inbox.count > 0 && <span>{inbox.count}</span>}
      </button>
      <button
        type="button"
        className={mobileView === 'map' ? 'is-active' : ''}
        onClick={() => onMobileViewChange('map')}
      >
        Map
      </button>
    </nav>
  );

  const expanded = (isMobile && mobileView === 'ideas') || mode === 'expanded';
  const showPanel = expanded && (!isMobile || mobileView === 'ideas');
  const showBubble = !showPanel && (
    mode === 'collapsed' || (isMobile && mobileView === 'map' && mode !== 'hidden')
  );

  return (
    <>
      {mobileTabs}
      {showBubble && (
        <button
          type="button"
          className={`idea-inbox__bubble ${isDropActive ? 'is-drop-active' : ''}`}
          data-export-ignore="true"
          data-viewport-occlusion="true"
          data-idea-inbox-drop-zone="true"
          onClick={() => {
            onModeChange('expanded');
            if (isMobile) onMobileViewChange('ideas');
          }}
          aria-label={`Open idea inbox with ${inbox.count} ${inbox.count === 1 ? 'idea' : 'ideas'}`}
        >
          <InboxIcon />
          <span>Inbox</span>
          <strong>{inbox.count}</strong>
        </button>
      )}
      {showPanel && (
        <aside
          className={`idea-inbox__panel ${isMobile ? 'is-mobile' : ''} ${isDropActive ? 'is-drop-active' : ''}`}
          data-dark={isDarkMode ? 'true' : 'false'}
          data-export-ignore="true"
          data-viewport-occlusion="true"
          data-idea-inbox-drop-zone="true"
          onPointerDown={stopCanvasEvent}
          onClick={stopCanvasEvent}
        >
          <header className="idea-inbox__header">
            <div>
              <InboxIcon size={20} />
              <span>
                <strong>{effectiveTarget ? `Ideas for “${effectiveTarget.text || 'Untitled'}”` : 'Idea inbox'}</strong>
                <small>
                  {effectiveTarget ? 'New ideas become direct children' : `${inbox.count} unplaced ${inbox.count === 1 ? 'idea' : 'ideas'}`}
                </small>
              </span>
            </div>
            <div>
              <button
                type="button"
                onClick={() => {
                  onModeChange('collapsed');
                  if (isMobile) onMobileViewChange('map');
                }}
                title="Collapse inbox"
                aria-label="Collapse idea inbox"
              >
                −
              </button>
              <button
                type="button"
                onClick={() => {
                  onModeChange('hidden');
                  if (isMobile) onMobileViewChange('map');
                }}
                title="Hide inbox"
                aria-label="Hide idea inbox"
              >
                ×
              </button>
            </div>
          </header>

          <div className="idea-inbox__composer">
            <textarea
              ref={composerRef}
              value={draft}
              disabled={!canWrite}
              onChange={(event) => updateDraft(event.target.value)}
              onKeyDown={(event) => {
                const isComposing = event.isComposing || event.nativeEvent?.isComposing;
                if (event.key !== 'Enter' || event.shiftKey || isComposing) return;
                event.preventDefault();
                submit();
              }}
              placeholder={canWrite ? 'Jot down an idea…' : 'You have view-only access'}
              rows={4}
            />
            <div>
              <span>
                {effectiveTarget ? `Adding under ${effectiveTarget.text || 'selected idea'}` : 'Adding to the inbox'}
              </span>
              <button type="button" disabled={!draft.trim() || submitting || !canWrite} onClick={submit}>
                {submitting ? 'Adding…' : 'Add idea'}
              </button>
            </div>
          </div>

          <div className="idea-inbox__list">
            {inbox.rootNodes.map((node) => (
              <InboxCard
                key={node.id}
                node={node}
                branchCount={inbox.branchCountByRootId.get(node.id) || 0}
                isMobile={isMobile}
                canWrite={canWrite}
                onDelete={onDeleteIdea}
                onDragStart={onInboxDragStart}
                onDragEnd={onInboxDragEnd}
              />
            ))}
            {!inbox.rootNodes.length && (
              <div className="idea-inbox__empty">
                <InboxIcon size={24} />
                <strong>Your inbox is clear</strong>
                <span>Capture a thought without deciding where it belongs yet.</span>
              </div>
            )}
          </div>
        </aside>
      )}
    </>
  );
}
